import React, { ReactNode, useEffect, useRef } from 'react'

import { BonusModal, BonusModalHeader, BonusModalBody, BonusModalFooter } from '@components'
import { BuildGradientStyle } from '@utils/buildGradientStyle'
import { Bonus, BonusSystemState } from 'game-ui-types'
import { useShrinkTextToFit } from '@amusnet/react-ui'
import { TranslationKey, useTranslation } from '@amusnet/translations/react'

interface BSModalTemplateProps {
    isModalOpened: boolean
    setIsModalOpened: (isOpened: boolean) => void
    titleKey: TranslationKey
    body: ReactNode
    footer: ReactNode
    titleColor: string
    gradient: BuildGradientStyle
    bonuses: Bonus[]
    bonusSystemState: string
    declinedBonusesIds?: string[]
}

const BSModalTemplate: React.FC<BSModalTemplateProps> = ({
    isModalOpened,
    setIsModalOpened,
    titleKey,
    body,
    footer,
    titleColor,
    gradient,
    bonuses,
    bonusSystemState,
    declinedBonusesIds,
}: BSModalTemplateProps) => {
    const { t } = useTranslation()
    const titleRef = useRef<HTMLParagraphElement | null>(null)
    const shouldHideModal =
        bonusSystemState === '' ||
        bonusSystemState === BonusSystemState.IN_PLAY ||
        bonusSystemState === BonusSystemState.INACTIVE

    const title = t(titleKey)

    useEffect(() => {
        setIsModalOpened(!shouldHideModal)
    }, [bonusSystemState])

    useEffect(() => {
        if (bonuses.length === declinedBonusesIds?.length) {
            setIsModalOpened(false)
        }
    }, [declinedBonusesIds])

    useShrinkTextToFit({
        refresh: [isModalOpened, title],
        designFontSize: 40,
        bounds: { width: 716 },
        textRef: titleRef,
    })

    return (
        <BonusModal isOpen={isModalOpened} showCloseIcon={false} closeOnOutsideClick={false}>
            <BonusModalHeader gradientStyle={gradient}>
                <p
                    id="bs-modal-title"
                    ref={titleRef}
                    className="text-[40px] font-bold py-0.5"
                    style={{ color: titleColor }}
                >
                    {title}
                </p>
            </BonusModalHeader>

            <BonusModalBody>{body}</BonusModalBody>

            <BonusModalFooter gradientStyle={gradient}>{footer}</BonusModalFooter>
        </BonusModal>
    )
}

export default BSModalTemplate
